import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Gift, Search, Filter, Plus, Package, HandHeart, X, MapPin, User,
  Truck, CheckCircle, MessageCircle, Camera, Tag, Pencil, Trash2, ShieldAlert, Share2, Euro
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== ITEM CARD =====
const MarketplaceCard = ({ item, language, onRequest, onEdit, onDelete, onAdminRemove, isAuthenticated, currentUserId, isAdmin }) => {
  const isOffer = item.type === 'offer';
  const isSell = item.type === 'sell';
  const isOwner = currentUserId === item.user_id;
  const shareUrl = `${API_URL}/api/share/marketplace/${item.item_id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden hover:shadow-md transition-shadow group"
      data-testid={`marketplace-item-${item.item_id}`}
    >
      {/* Image - clickable to detail */}
      <Link to={`/marketplace/${item.item_id}`}>
      {item.images?.length > 0 ? (
        <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
          <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {/* Price badge for sell items */}
          {isSell && item.price && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-amber-500 text-white text-sm font-bold shadow-lg px-3 py-1">
                <Euro className="h-3.5 w-3.5 mr-0.5" />{item.price.toFixed(2)}
              </Badge>
            </div>
          )}
          {/* Owner actions overlay */}
          {isOwner && (
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => onEdit(item)} data-testid={`edit-item-${item.item_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors">
                <Pencil className="h-3.5 w-3.5 text-stone-600" />
              </button>
              <button onClick={() => onDelete(item)} data-testid={`delete-item-${item.item_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-red-50 transition-colors">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          )}
          {/* Admin remove button */}
          {isAdmin && !isOwner && (
            <div className="absolute top-2 right-2">
              <button onClick={() => onAdminRemove(item)} data-testid={`admin-remove-item-${item.item_id}`}
                className="p-2 bg-red-500/90 backdrop-blur-sm rounded-full shadow hover:bg-red-600 transition-colors">
                <ShieldAlert className="h-3.5 w-3.5 text-white" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-[4/3] bg-stone-50 dark:bg-stone-700 flex items-center justify-center relative">
          {isSell ? <Euro className="h-12 w-12 text-stone-300" /> : isOffer ? <Gift className="h-12 w-12 text-stone-300" /> : <HandHeart className="h-12 w-12 text-stone-300" />}
          {isSell && item.price && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-amber-500 text-white text-sm font-bold shadow-lg px-3 py-1">
                <Euro className="h-3.5 w-3.5 mr-0.5" />{item.price.toFixed(2)}
              </Badge>
            </div>
          )}
          {isOwner && (
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => onEdit(item)} data-testid={`edit-item-${item.item_id}`}
                className="p-2 bg-white/90 rounded-full shadow hover:bg-white transition-colors">
                <Pencil className="h-3.5 w-3.5 text-stone-600" />
              </button>
              <button onClick={() => onDelete(item)} data-testid={`delete-item-${item.item_id}`}
                className="p-2 bg-white/90 rounded-full shadow hover:bg-red-50 transition-colors">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          )}
          {isAdmin && !isOwner && (
            <div className="absolute top-2 right-2">
              <button onClick={() => onAdminRemove(item)} data-testid={`admin-remove-item-${item.item_id}`}
                className="p-2 bg-red-500/90 rounded-full shadow hover:bg-red-600 transition-colors">
                <ShieldAlert className="h-3.5 w-3.5 text-white" />
              </button>
            </div>
          )}
        </div>
      )}
      </Link>

      <div className="p-4">
        {/* Type + Category + City badges */}
        <Link to={`/marketplace/${item.item_id}`} className="block">
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <Badge className={isSell ? 'bg-amber-100 text-amber-700 text-xs' : isOffer ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
            {isSell ? (language === 'it' ? 'In vendita' : 'For sale') : isOffer ? (language === 'it' ? 'Regalo' : 'Giving away') : (language === 'it' ? 'Cerco' : 'Looking for')}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            <Tag className="h-3 w-3 mr-1" />{item.category_name || item.category}
          </Badge>
          {item.city && (
            <Badge variant="outline" className="text-xs text-stone-500">
              <MapPin className="h-3 w-3 mr-1" />{item.city}
            </Badge>
          )}
        </div>

        <h3 className="font-heading font-semibold text-stone-900 dark:text-white text-base mb-1 line-clamp-2">{item.title}</h3>
        <p className="text-stone-500 dark:text-stone-400 text-sm line-clamp-2 mb-3">{item.description}</p>
        </Link>

        {/* Shipping info */}
        {(isOffer || isSell) && item.shipping_description && (
          <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-3">
            <Truck className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{item.shipping_description}</span>
            <Badge variant="outline" className="text-[10px] ml-auto shrink-0">
              {item.shipping_paid_by === 'donor' ? (language === 'it' ? 'Gratis' : 'Free') : (language === 'it' ? 'A carico' : 'Paid')}
            </Badge>
          </div>
        )}

        {/* User + Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-100 dark:border-stone-700">
          <Link to={`/profile/${item.user_id}`} className="flex items-center gap-2 min-w-0">
            <Avatar className="h-7 w-7">
              <AvatarImage src={item.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${item.user_id}`} />
              <AvatarFallback className="text-xs bg-amber-100 text-amber-700">{item.user_name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="text-sm text-stone-600 dark:text-stone-400 truncate">{item.user_name}</span>
            {item.is_verified && <CheckCircle className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
          </Link>

          {isAuthenticated && !isOwner && (
            <div className="flex gap-1.5">
              <Button size="sm" onClick={() => onRequest(item)}
                data-testid={`chat-item-${item.item_id}`}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-xs px-4">
                <MessageCircle className="h-3.5 w-3.5 mr-1" />
                {language === 'it' ? 'Chatta' : 'Chat'}
              </Button>
            </div>
          )}
        </div>
        {/* Share buttons */}
        <div className="pt-2 flex justify-end">
          <div className="scale-75 origin-right">
            <ShareButtons url={shareUrl} title={item.title} description={item.description} language={language} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ===== CREATE/EDIT ITEM FORM =====
const ItemForm = ({ language, token, categories, onSuccess, editItem }) => {
  const [type, setType] = useState(editItem?.type || 'offer');
  const [title, setTitle] = useState(editItem?.title || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [category, setCategory] = useState(editItem?.category || '');
  const [city, setCity] = useState(editItem?.city || '');
  const [images, setImages] = useState(editItem?.images || []);
  const [shippingDesc, setShippingDesc] = useState(editItem?.shipping_description || '');
  const [shippingPaidBy, setShippingPaidBy] = useState(editItem?.shipping_paid_by || 'donor');
  const [price, setPrice] = useState(editItem?.price || '');
  const [loading, setLoading] = useState(false);
  const imageInputRef = useRef(null);
  const isEditing = !!editItem;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error(language === 'it' ? 'Massimo 5 immagini' : 'Maximum 5 images');
      return;
    }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onloadend = () => setImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !category) {
      toast.error(language === 'it' ? 'Compila tutti i campi obbligatori' : 'Fill all required fields');
      return;
    }
    if (images.length === 0) {
      toast.error(language === 'it' ? 'Aggiungi almeno un\'immagine di copertina' : 'Add at least one cover image');
      return;
    }
    if (type === 'sell' && (!price || parseFloat(price) <= 0)) {
      toast.error(language === 'it' ? 'Inserisci un prezzo valido' : 'Enter a valid price');
      return;
    }
    setLoading(true);
    try {
      const data = {
        type, title: title.trim(), description: description.trim(),
        category, images, city: city.trim(),
        shipping_description: type !== 'request' ? shippingDesc.trim() : null,
        shipping_paid_by: type !== 'request' ? shippingPaidBy : null,
        price: type === 'sell' ? parseFloat(price) : null
      };
      if (isEditing) {
        await axios.put(`${API_URL}/api/marketplace/items/${editItem.item_id}`, data,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'it' ? 'Annuncio aggiornato!' : 'Listing updated!');
      } else {
        await axios.post(`${API_URL}/api/marketplace/items`, data,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'it' ? 'Annuncio pubblicato!' : 'Listing published!');
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Type selector (only for new items) */}
      {!isEditing && (
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => setType('offer')} data-testid="type-offer-btn"
            className={`p-4 rounded-xl border-2 text-center transition-all ${type === 'offer' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'}`}>
            <Gift className={`h-6 w-6 mx-auto mb-2 ${type === 'offer' ? 'text-emerald-600' : 'text-stone-400'}`} />
            <p className={`font-semibold text-sm ${type === 'offer' ? 'text-emerald-700' : 'text-stone-600'}`}>
              {language === 'it' ? 'Voglio regalare' : 'I want to give away'}
            </p>
          </button>
          <button onClick={() => setType('sell')} data-testid="type-sell-btn"
            className={`p-4 rounded-xl border-2 text-center transition-all ${type === 'sell' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}>
            <Euro className={`h-6 w-6 mx-auto mb-2 ${type === 'sell' ? 'text-amber-600' : 'text-stone-400'}`} />
            <p className={`font-semibold text-sm ${type === 'sell' ? 'text-amber-700' : 'text-stone-600'}`}>
              {language === 'it' ? 'Voglio vendere' : 'I want to sell'}
            </p>
          </button>
          <button onClick={() => setType('request')} data-testid="type-request-btn"
            className={`p-4 rounded-xl border-2 text-center transition-all ${type === 'request' ? 'border-rose-500 bg-rose-50' : 'border-stone-200 hover:border-stone-300'}`}>
            <HandHeart className={`h-6 w-6 mx-auto mb-2 ${type === 'request' ? 'text-rose-600' : 'text-stone-400'}`} />
            <p className={`font-semibold text-sm ${type === 'request' ? 'text-rose-700' : 'text-stone-600'}`}>
              {language === 'it' ? 'Cerco un oggetto' : 'Looking for an item'}
            </p>
          </button>
        </div>
      )}

      <div className="space-y-2">
        <Label className="font-semibold">{language === 'it' ? 'Titolo *' : 'Title *'}</Label>
        <Input data-testid="item-title-input" value={title} onChange={e => setTitle(e.target.value)}
          placeholder={type === 'offer' ? (language === 'it' ? 'Es: Laptop HP in buone condizioni' : 'E.g.: HP Laptop in good condition') : (language === 'it' ? 'Es: Cerco passeggino per bambino' : 'E.g.: Looking for baby stroller')}
          className="h-12 rounded-xl" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="font-semibold">{language === 'it' ? 'Categoria *' : 'Category *'}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="item-category-select" className="h-12 rounded-xl">
              <SelectValue placeholder={language === 'it' ? 'Seleziona' : 'Select'} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>{language === 'it' ? c.name_it : c.name_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="font-semibold">
            <MapPin className="h-3.5 w-3.5 inline mr-1" />
            {language === 'it' ? 'Città' : 'City'}
          </Label>
          <Input data-testid="item-city-input" value={city} onChange={e => setCity(e.target.value)}
            placeholder={language === 'it' ? 'Es: Roma, Milano...' : 'E.g.: Rome, Milan...'}
            className="h-12 rounded-xl" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{language === 'it' ? 'Descrizione *' : 'Description *'}</Label>
        <Textarea data-testid="item-description-input" value={description} onChange={e => setDescription(e.target.value)}
          placeholder={type === 'offer' ? (language === 'it' ? 'Descrivi l\'oggetto: condizioni, dimensioni, difetti...' : 'Describe the item: condition, size, defects...') : (language === 'it' ? 'Descrivi cosa cerchi e perché ne hai bisogno...' : 'Describe what you need and why...')}
          className="min-h-[120px] rounded-xl" />
      </div>

      {/* Photos */}
      <div className="space-y-2">
        <Label className="font-semibold">{language === 'it' ? 'Foto di copertina * (max 5)' : 'Cover photos * (max 5)'}</Label>
        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" ref={imageInputRef} />
        <div className="flex flex-wrap gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <img src={img} alt="" className="h-20 w-20 object-cover rounded-xl border-2 border-stone-200" />
              <button type="button" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {images.length < 5 && (
            <button type="button" onClick={() => imageInputRef.current?.click()}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-emerald-500 hover:text-emerald-600 transition-colors">
              <Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">{language === 'it' ? 'Aggiungi' : 'Add'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Price (only for sell) */}
      {type === 'sell' && (
        <div className="space-y-2">
          <Label className="font-semibold">
            <Euro className="h-3.5 w-3.5 inline mr-1" />
            {language === 'it' ? 'Prezzo (€) *' : 'Price (€) *'}
          </Label>
          <Input data-testid="item-price-input" type="number" min="0.01" step="0.01" value={price}
            onChange={e => setPrice(e.target.value)}
            placeholder={language === 'it' ? 'Es: 15.00' : 'E.g.: 15.00'}
            className="h-12 rounded-xl" />
        </div>
      )}

      {/* Shipping (for offers and sell) */}
      {type !== 'request' && (
        <div className="space-y-4 p-4 bg-stone-50 rounded-xl">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-stone-600" />
            <Label className="font-semibold">{language === 'it' ? 'Spedizione' : 'Shipping'}</Label>
          </div>
          <Input value={shippingDesc} onChange={e => setShippingDesc(e.target.value)} data-testid="item-shipping-desc"
            placeholder={language === 'it' ? 'Es: Ritiro a mano a Milano oppure spedizione con corriere' : 'E.g.: Pickup in Milan or courier shipping'}
            className="h-12 rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setShippingPaidBy('donor')} data-testid="shipping-donor-btn"
              className={`p-3 rounded-xl border text-sm text-center transition-all ${shippingPaidBy === 'donor' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-stone-200 text-stone-600'}`}>
              {language === 'it' ? 'Donatore (gratis)' : 'Donor (free)'}
            </button>
            <button onClick={() => setShippingPaidBy('recipient')} data-testid="shipping-recipient-btn"
              className={`p-3 rounded-xl border text-sm text-center transition-all ${shippingPaidBy === 'recipient' ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold' : 'border-stone-200 text-stone-600'}`}>
              {language === 'it' ? 'Destinatario' : 'Recipient'}
            </button>
          </div>
        </div>
      )}

      <Button onClick={handleSubmit} disabled={loading} data-testid="publish-item-btn"
        className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-semibold">
        {loading ? '...' : isEditing
          ? (language === 'it' ? 'Salva modifiche' : 'Save changes')
          : (language === 'it' ? 'Pubblica annuncio' : 'Publish listing')}
      </Button>
    </div>
  );
};

// ===== MAIN PAGE =====
const Marketplace = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightItemId = searchParams.get('item');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchItems(); }, [activeTab, selectedCategory]);

  // Scroll to highlighted item from share link
  useEffect(() => {
    if (highlightItemId && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="marketplace-item-${highlightItemId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-amber-500', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-500', 'ring-offset-2'), 4000);
        }
      }, 300);
    }
  }, [highlightItemId, loading]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/categories`);
      setCategories(res.data.categories);
    } catch {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (activeTab === 'mine') {
        params.user_id = user?.user_id;
      } else {
        if (activeTab !== 'all') params.type = activeTab;
      }
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (cityFilter.trim()) params.city = cityFilter.trim();
      const res = await axios.get(`${API_URL}/api/marketplace/items`, { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchItems(); };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? (language === 'it' ? cat.name_it : cat.name_en) : catId;
  };

  const handleRequestItem = async (item) => {
    try {
      const res = await axios.post(`${API_URL}/api/marketplace/items/${item.item_id}/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Messaggio inviato! Vai nella Chat.' : 'Message sent! Go to Chat.');
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(language === 'it' ? 'Sei sicuro di voler eliminare questo annuncio?' : 'Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API_URL}/api/marketplace/items/${item.item_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Annuncio eliminato' : 'Listing deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleAdminRemove = async (item) => {
    if (!window.confirm(language === 'it' ? 'Rimuovere questo annuncio per violazione delle regole?' : 'Remove this listing for rule violation?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/marketplace/${item.item_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'it' ? 'Annuncio rimosso dall\'admin' : 'Listing removed by admin');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
  };

  const enrichedItems = items.map(item => ({ ...item, category_name: getCategoryName(item.category) }));

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-teal-700 to-emerald-800 rounded-3xl p-6 md:p-10 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-teal-600/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Package className="h-7 w-7" />
                <span className="font-medium text-teal-200">{language === 'it' ? 'Mercatino Solidale' : 'Solidarity Marketplace'}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2" data-testid="marketplace-title">
                {language === 'it' ? 'Regala e ricevi oggetti' : 'Give and receive items'}
              </h1>
              <p className="text-teal-100 max-w-lg">
                {language === 'it' ? 'Dai una seconda vita ai tuoi oggetti o trova ciò di cui hai bisogno. Tutto gratis, tutto solidale.' : 'Give your items a second life or find what you need. Everything free, everything solidary.'}
              </p>
            </div>
            {isAuthenticated && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <Button data-testid="create-listing-btn" className="bg-white text-teal-800 hover:bg-teal-50 rounded-full px-6 py-3 font-semibold shadow-lg shrink-0">
                    <Plus className="h-5 w-5 mr-2" />{language === 'it' ? 'Pubblica annuncio' : 'Create listing'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">{language === 'it' ? 'Nuovo annuncio' : 'New listing'}</DialogTitle>
                  </DialogHeader>
                  <ItemForm language={language} token={token} categories={categories}
                    onSuccess={() => { setShowCreate(false); fetchItems(); }} />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
              <TabsList className={`grid w-full md:w-auto ${isAuthenticated ? 'grid-cols-5' : 'grid-cols-4'}`}>
                <TabsTrigger value="all" data-testid="tab-all">{language === 'it' ? 'Tutti' : 'All'}</TabsTrigger>
                <TabsTrigger value="offer" data-testid="tab-offers"><Gift className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Regali' : 'Gifts'}</TabsTrigger>
                <TabsTrigger value="sell" data-testid="tab-sell"><Euro className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Vendita' : 'For Sale'}</TabsTrigger>
                <TabsTrigger value="request" data-testid="tab-requests"><HandHeart className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Richieste' : 'Requests'}</TabsTrigger>
                {isAuthenticated && (
                  <TabsTrigger value="mine" data-testid="tab-mine"><User className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'I miei' : 'Mine'}</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="marketplace-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={language === 'it' ? 'Cerca oggetti...' : 'Search items...'}
                className="w-full pl-9 pr-4 py-2.5 bg-stone-50 rounded-xl text-sm border border-stone-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:bg-white" />
            </form>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="marketplace-city-filter" value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                onBlur={fetchItems}
                onKeyDown={e => e.key === 'Enter' && fetchItems()}
                placeholder={language === 'it' ? 'Filtra per città...' : 'Filter by city...'}
                className="w-full md:w-[180px] pl-9 pr-4 py-2.5 bg-stone-50 rounded-xl text-sm border border-stone-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:bg-white" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="marketplace-category-filter" className="w-full md:w-[180px] rounded-xl">
                <Filter className="h-4 w-4 mr-2 text-stone-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'it' ? 'Tutte le categorie' : 'All categories'}</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{language === 'it' ? c.name_it : c.name_en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-stone-600 text-sm">{total} {language === 'it' ? 'annunci trovati' : 'listings found'}</p>
          {!isAuthenticated && (
            <Link to="/register">
              <Button variant="outline" size="sm" className="rounded-full text-xs">{language === 'it' ? 'Registrati per pubblicare' : 'Register to publish'}</Button>
            </Link>
          )}
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : enrichedItems.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 mb-2" data-testid="no-items-message">
              {language === 'it' ? 'Nessun annuncio trovato' : 'No listings found'}
            </h3>
            <p className="text-stone-500 mb-6">
              {language === 'it' ? 'Sii il primo a pubblicare un annuncio nel Mercatino Solidale!' : 'Be the first to publish a listing in the Solidarity Marketplace!'}
            </p>
            {isAuthenticated && (
              <Button onClick={() => setShowCreate(true)} className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-6">
                <Plus className="h-5 w-5 mr-2" />{language === 'it' ? 'Pubblica annuncio' : 'Create listing'}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {enrichedItems.map(item => (
              <MarketplaceCard key={item.item_id} item={item} language={language}
                onRequest={handleRequestItem} onEdit={handleEditItem}
                onDelete={handleDeleteItem} onAdminRemove={handleAdminRemove}
                isAuthenticated={isAuthenticated} currentUserId={user?.user_id} isAdmin={isAdmin} />
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) setEditingItem(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{language === 'it' ? 'Modifica annuncio' : 'Edit listing'}</DialogTitle>
            </DialogHeader>
            {editingItem && (
              <ItemForm language={language} token={token} categories={categories} editItem={editingItem}
                onSuccess={() => { setEditingItem(null); fetchItems(); }} />
            )}
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default Marketplace;
